import { cx } from "@emotion/css";
import parse, {
	type DOMNode,
	domToReact,
	Element,
	type HTMLReactParserOptions,
} from "html-react-parser";
import { type MouseEvent, useMemo } from "react";
import { ScrollShadowWrapper } from "src/components/Default/LekUI/ScrollShadowWrapper";
import "src/styles/tiptapEditor.scss";

interface TiptapContentProps {
	/** HTML zapisany przez edytor TipTap (artykuły, blog, omówienia…). */
	html: string;
	className?: string;
	onClick?: (e: MouseEvent<HTMLDivElement>) => void;
}

// szeroka tabela: szerokość treści + scroll w bok z cieniami (jak stary widok przez htmlFormatter)
const parserOptions: HTMLReactParserOptions = {
	replace: (node) => {
		if (node instanceof Element && node.name === "table") {
			return (
				<ScrollShadowWrapper>
					{domToReact([node as DOMNode])}
				</ScrollShadowWrapper>
			);
		}
	},
};

/** Treść z edytora TipTap 1:1 jak w edytorze (.tiptap-content). Każdy podgląd HTML z TipTapa renderuj tym. */
export function TiptapContent({
	html,
	className,
	onClick,
}: TiptapContentProps) {
	const content = useMemo(
		() => parse(prepareTiptapHtml(html), parserOptions),
		[html],
	);

	return (
		<div className={cx("tiptap-content", className)} onClick={onClick}>
			{content}
		</div>
	);
}

// tiptap-extension-resize-image trzyma szerokosc i wyrownanie (margin) w atrybucie containerstyle,
// ktory czyta tylko edytor; przenosimy je do style, zeby widok wygladal jak edytor
function prepareTiptapHtml(html: string) {
	const doc = new DOMParser().parseFromString(html, "text/html");
	for (const img of doc.querySelectorAll<HTMLImageElement>(
		"img[containerstyle]",
	)) {
		img.style.cssText = `${img.getAttribute("containerstyle")}; display: block;`;
		img.style.removeProperty("cursor");
		img.removeAttribute("containerstyle");
	}
	// opakowanie tabeli ze starej Froali (overflow-x: scroll → pusty pasek przewijania); tabelę owija ScrollShadowWrapper
	for (const wrapper of doc.querySelectorAll("div.table-wrapper")) {
		wrapper.replaceWith(...Array.from(wrapper.childNodes));
	}
	// puste akapity (też w komórkach tabel) robią puste linie — usuwamy je jak htmlFormatter
	for (const p of doc.querySelectorAll("p")) {
		if (isEmptyNode(p)) p.remove();
	}
	// treść komórki w .tiptap-cell: minimalna szerokość komórki (tiptapEditor.scss)
	for (const cell of doc.querySelectorAll<HTMLTableCellElement>("td, th")) {
		// wyrównanie komórek ze starego edytora (Froala); edytor TipTap je wyrzuca, więc podgląd ma je do lewej i do góry
		cell.style.removeProperty("text-align");
		cell.style.removeProperty("vertical-align");
		const wrapper = doc.createElement("div");
		wrapper.className = "tiptap-cell";
		wrapper.append(...Array.from(cell.childNodes));
		cell.append(wrapper);
	}
	return doc.body.innerHTML;
}

// ta sama definicja "pustego" co isEmptyParagraph w src/formatters/htmlFormatter.jsx
const INLINE_WRAPPERS = new Set([
	"P",
	"SPAN",
	"EM",
	"STRONG",
	"B",
	"I",
	"U",
	"FONT",
	"SMALL",
	"MARK",
]);

function isEmptyNode(node: Node): boolean {
	if (node.nodeType === Node.TEXT_NODE) {
		return !node.textContent?.replace(/ /g, " ").trim();
	}
	if (node.nodeName === "BR") return true;
	if (INLINE_WRAPPERS.has(node.nodeName)) {
		return Array.from(node.childNodes).every(isEmptyNode);
	}
	return false;
}
